import { UpdatePostDto } from '@/schemas';
import { FormatPostDto } from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FacebookPublishRequest,
  InstagramPublishRequest,
  LinkedInPublishRequest,
  SocialChannels,
} from '@/types/posts';
import { QueryKey } from '@/types/queryKey';
import { SocialMediaType } from '@/types/settings.ts';
import {
  formatPost,
  getFormattedPosts,
  getPost,
  getPosts,
  publishToSocialMedia,
  updateFormattedPost,
  updatePost,
} from '@/lib/services/posts';

export function useGetPost(projectId: string, postId: string) {
  return useQuery({
    queryKey: [QueryKey.Post, postId],
    queryFn: () => getPost(projectId, postId),
    enabled: !!projectId && !!postId,
  });
}
export function useGetPosts(
  projectId: string,
  page_size?: number,
  page?: number
) {
  return useQuery({
    queryKey: [QueryKey.Post, page],
    queryFn: () => getPosts(projectId, page_size, page),
    enabled: !!projectId,
  });
}

export function useFormatPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: FormatPostDto) => formatPost(dto),
    onSuccess: (_, variables) => {
      // Invalidate the main posts list
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Post],
      });

      // Invalidate the cache for all formats of this post
      variables.formats.forEach((format) => {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Post, format, variables.postId],
        });
      });
    },
  });
}

export function useGetFormattedPosts(
  projectId: string,
  postId: string,
  postFormat: SocialChannels
) {
  return useQuery({
    queryKey: [QueryKey.Post, postFormat, postId],
    queryFn: () => getFormattedPosts(projectId, postId, postFormat),
    enabled: !!postFormat && !!postId,
    staleTime: Infinity,
  });
}

export function useUpdatePost(postId: string, projectId: string) {
  return useMutation({
    mutationFn: (dto: UpdatePostDto) => updatePost(projectId, postId, dto),
    mutationKey: [QueryKey.Post, postId],
  });
}

/**
 * Hook for publishing a post to social media
 */
export function usePublishToSocialMedia() {
  return useMutation({
    mutationFn: ({
      projectId,
      postId,
      socialChannel,
      data,
    }: {
      projectId: string;
      postId: string;
      socialChannel: SocialMediaType;
      data:
        | InstagramPublishRequest
        | FacebookPublishRequest
        | LinkedInPublishRequest;
    }) => publishToSocialMedia(projectId, postId, socialChannel, data),
  });
}

export function useInvalidateFormattedPostCache() {
  const queryClient = useQueryClient();

  return (postId: string, format?: SocialChannels) => {
    // Always invalidate the main posts list
    // queryClient.invalidateQueries({
    //   queryKey: [QueryKey.Post],
    // });

    if (format) {
      // Invalidate specific format
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Post, format, postId],
      });
    } else {
      // Invalidate all formats for this post
      Object.values(SocialChannels).forEach((format) => {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Post, format, postId],
        });
      });
    }
  };
}

export function useUpdateFormattedPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      postId,
      format,
      content,
    }: {
      projectId: string;
      postId: string;
      format: string;
      content: string;
    }) => updateFormattedPost(projectId, postId, format, content),
    onSuccess: (_, variables) => {
      // Invalidate the cache for this specific format
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Post, variables.format, variables.postId],
      });
    },
  });
}
